export type GroupType = 'campo' | 'society' | 'quadra';
export type FormationType = 'ofensiva' | 'defensiva' | 'neutra';
export type PlayerPosition = 'goleiro' | 'zagueiro' | 'lateral' | 'meio-campo' | 'atacante';

export interface FieldPosition {
  id: string;
  name: string;
  x: number; // Posição X no campo (0-100)
  y: number; // Posição Y no campo (0-100)
  position: PlayerPosition;
  required: boolean;
}

export interface Formation {
  name: string;
  totalPlayers: number;
  positions: {
    zagueiro: number;
    lateral: number;
    meioCampo: number;
    atacante: number;
  };
  description: string;
  fieldPositions: FieldPosition[];
}

export interface FieldConfig {
  playersPerTeam: number;
  requiredPositions: PlayerPosition[];
  formations: {
    ofensiva: Formation[];
    defensiva: Formation[];
    neutra: Formation[];
  };
}

export const fieldConfigurations: Record<GroupType, FieldConfig> = {
  campo: {
    playersPerTeam: 11,
    requiredPositions: ['goleiro', 'zagueiro', 'lateral', 'meio-campo', 'atacante'],
    formations: {
      ofensiva: [
        {
          name: '4-3-3',
          totalPlayers: 10,
          positions: { zagueiro: 2, lateral: 2, meioCampo: 3, atacante: 3 },
          description: 'Foco no ataque com pontas abertos',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Zagueiro Esquerdo', x: 35, y: 20, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Zagueiro Direito', x: 65, y: 20, position: 'zagueiro', required: true },
            { id: 'lat_l', name: 'Lateral Esquerdo', x: 15, y: 25, position: 'lateral', required: true },
            { id: 'lat_r', name: 'Lateral Direito', x: 85, y: 25, position: 'lateral', required: true },
            { id: 'mc_l', name: 'Meio-campo Esquerdo', x: 30, y: 45, position: 'meio-campo', required: true },
            { id: 'mc_c', name: 'Meio-campo Central', x: 50, y: 40, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Meio-campo Direito', x: 70, y: 45, position: 'meio-campo', required: true },
            { id: 'at_l', name: 'Atacante Esquerdo', x: 25, y: 75, position: 'atacante', required: true },
            { id: 'at_c', name: 'Atacante Central', x: 50, y: 80, position: 'atacante', required: true },
            { id: 'at_r', name: 'Atacante Direito', x: 75, y: 75, position: 'atacante', required: true },
          ]
        },
        {
          name: '3-4-3',
          totalPlayers: 10,
          positions: { zagueiro: 3, lateral: 0, meioCampo: 4, atacante: 3 },
          description: 'Formação agressiva com laterais no meio-campo',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Zagueiro Esquerdo', x: 25, y: 20, position: 'zagueiro', required: true },
            { id: 'zag_c', name: 'Zagueiro Central', x: 50, y: 15, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Zagueiro Direito', x: 75, y: 20, position: 'zagueiro', required: true },
            { id: 'mc_l', name: 'Meio-campo Esquerdo', x: 20, y: 45, position: 'meio-campo', required: true },
            { id: 'mc_cl', name: 'Meio-campo Central Esq', x: 40, y: 40, position: 'meio-campo', required: true },
            { id: 'mc_cr', name: 'Meio-campo Central Dir', x: 60, y: 40, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Meio-campo Direito', x: 80, y: 45, position: 'meio-campo', required: true },
            { id: 'at_l', name: 'Atacante Esquerdo', x: 25, y: 75, position: 'atacante', required: true },
            { id: 'at_c', name: 'Atacante Central', x: 50, y: 80, position: 'atacante', required: true },
            { id: 'at_r', name: 'Atacante Direito', x: 75, y: 75, position: 'atacante', required: true },
          ]
        }
      ],
      defensiva: [
        {
          name: '5-4-1',
          totalPlayers: 10,
          positions: { zagueiro: 3, lateral: 2, meioCampo: 4, atacante: 1 },
          description: 'Defesa sólida com contra-ataques',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Zagueiro Esquerdo', x: 30, y: 20, position: 'zagueiro', required: true },
            { id: 'zag_c', name: 'Zagueiro Central', x: 50, y: 15, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Zagueiro Direito', x: 70, y: 20, position: 'zagueiro', required: true },
            { id: 'lat_l', name: 'Lateral Esquerdo', x: 10, y: 30, position: 'lateral', required: true },
            { id: 'lat_r', name: 'Lateral Direito', x: 90, y: 30, position: 'lateral', required: true },
            { id: 'mc_l', name: 'Meio-campo Esquerdo', x: 30, y: 50, position: 'meio-campo', required: true },
            { id: 'mc_cl', name: 'Meio-campo Central Esq', x: 45, y: 45, position: 'meio-campo', required: true },
            { id: 'mc_cr', name: 'Meio-campo Central Dir', x: 55, y: 45, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Meio-campo Direito', x: 70, y: 50, position: 'meio-campo', required: true },
            { id: 'at_c', name: 'Atacante Central', x: 50, y: 75, position: 'atacante', required: true },
          ]
        }
      ],
      neutra: [
        {
          name: '4-4-2',
          totalPlayers: 10,
          positions: { zagueiro: 2, lateral: 2, meioCampo: 4, atacante: 2 },
          description: 'Equilíbrio entre defesa e ataque',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Zagueiro Esquerdo', x: 35, y: 20, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Zagueiro Direito', x: 65, y: 20, position: 'zagueiro', required: true },
            { id: 'lat_l', name: 'Lateral Esquerdo', x: 15, y: 25, position: 'lateral', required: true },
            { id: 'lat_r', name: 'Lateral Direito', x: 85, y: 25, position: 'lateral', required: true },
            { id: 'mc_l', name: 'Meio-campo Esquerdo', x: 30, y: 50, position: 'meio-campo', required: true },
            { id: 'mc_cl', name: 'Meio-campo Central Esq', x: 45, y: 45, position: 'meio-campo', required: true },
            { id: 'mc_cr', name: 'Meio-campo Central Dir', x: 55, y: 45, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Meio-campo Direito', x: 70, y: 50, position: 'meio-campo', required: true },
            { id: 'at_l', name: 'Atacante Esquerdo', x: 40, y: 75, position: 'atacante', required: true },
            { id: 'at_r', name: 'Atacante Direito', x: 60, y: 75, position: 'atacante', required: true },
          ]
        },
        {
          name: '4-2-3-1',
          totalPlayers: 10,
          positions: { zagueiro: 2, lateral: 2, meioCampo: 5, atacante: 1 },
          description: 'Meio-campo forte com um atacante central',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Zagueiro Esquerdo', x: 35, y: 20, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Zagueiro Direito', x: 65, y: 20, position: 'zagueiro', required: true },
            { id: 'lat_l', name: 'Lateral Esquerdo', x: 15, y: 25, position: 'lateral', required: true },
            { id: 'lat_r', name: 'Lateral Direito', x: 85, y: 25, position: 'lateral', required: true },
            { id: 'vol_l', name: 'Volante Esquerdo', x: 40, y: 40, position: 'meio-campo', required: true },
            { id: 'vol_r', name: 'Volante Direito', x: 60, y: 40, position: 'meio-campo', required: true },
            { id: 'mc_l', name: 'Meia Esquerdo', x: 25, y: 60, position: 'meio-campo', required: true },
            { id: 'mc_c', name: 'Meia Central', x: 50, y: 65, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Meia Direito', x: 75, y: 60, position: 'meio-campo', required: true },
            { id: 'at_c', name: 'Atacante Central', x: 50, y: 80, position: 'atacante', required: true },
          ]
        }
      ]
    }
  },
  society: {
    playersPerTeam: 7,
    requiredPositions: ['goleiro', 'zagueiro', 'meio-campo', 'atacante'],
    formations: {
      ofensiva: [
        {
          name: '2-3-1',
          totalPlayers: 6,
          positions: { zagueiro: 2, lateral: 0, meioCampo: 3, atacante: 1 },
          description: 'Pressão alta e ataque rápido',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Zagueiro Esquerdo', x: 35, y: 25, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Zagueiro Direito', x: 65, y: 25, position: 'zagueiro', required: true },
            { id: 'mc_l', name: 'Meio-campo Esquerdo', x: 25, y: 50, position: 'meio-campo', required: true },
            { id: 'mc_c', name: 'Meio-campo Central', x: 50, y: 45, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Meio-campo Direito', x: 75, y: 50, position: 'meio-campo', required: true },
            { id: 'at_c', name: 'Atacante Central', x: 50, y: 75, position: 'atacante', required: true },
          ]
        }
      ],
      defensiva: [
        {
          name: '3-2-1',
          totalPlayers: 6,
          positions: { zagueiro: 3, lateral: 0, meioCampo: 2, atacante: 1 },
          description: 'Defesa reforçada com contra-ataques',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Zagueiro Esquerdo', x: 25, y: 25, position: 'zagueiro', required: true },
            { id: 'zag_c', name: 'Zagueiro Central', x: 50, y: 20, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Zagueiro Direito', x: 75, y: 25, position: 'zagueiro', required: true },
            { id: 'mc_l', name: 'Meio-campo Esquerdo', x: 35, y: 50, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Meio-campo Direito', x: 65, y: 50, position: 'meio-campo', required: true },
            { id: 'at_c', name: 'Atacante Central', x: 50, y: 75, position: 'atacante', required: true },
          ]
        }
      ],
      neutra: [
        {
          name: '2-2-2',
          totalPlayers: 6,
          positions: { zagueiro: 2, lateral: 0, meioCampo: 2, atacante: 2 },
          description: 'Equilíbrio entre setores',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Zagueiro Esquerdo', x: 35, y: 25, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Zagueiro Direito', x: 65, y: 25, position: 'zagueiro', required: true },
            { id: 'mc_l', name: 'Meio-campo Esquerdo', x: 35, y: 50, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Meio-campo Direito', x: 65, y: 50, position: 'meio-campo', required: true },
            { id: 'at_l', name: 'Atacante Esquerdo', x: 40, y: 75, position: 'atacante', required: true },
            { id: 'at_r', name: 'Atacante Direito', x: 60, y: 75, position: 'atacante', required: true },
          ]
        }
      ]
    }
  },
  quadra: {
    playersPerTeam: 5,
    requiredPositions: ['goleiro', 'zagueiro', 'atacante'],
    formations: {
      ofensiva: [
        {
          name: '2-2',
          totalPlayers: 4,
          positions: { zagueiro: 2, lateral: 0, meioCampo: 0, atacante: 2 },
          description: 'Dois defensores e dois atacantes',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Fixo Esquerdo', x: 35, y: 30, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Fixo Direito', x: 65, y: 30, position: 'zagueiro', required: true },
            { id: 'at_l', name: 'Ala Esquerdo', x: 35, y: 70, position: 'atacante', required: true },
            { id: 'at_r', name: 'Ala Direito', x: 65, y: 70, position: 'atacante', required: true },
          ]
        }
      ],
      defensiva: [
        {
          name: '3-1',
          totalPlayers: 4,
          positions: { zagueiro: 3, lateral: 0, meioCampo: 0, atacante: 1 },
          description: 'Defesa forte com um pivô',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_l', name: 'Fixo Esquerdo', x: 25, y: 30, position: 'zagueiro', required: true },
            { id: 'zag_c', name: 'Fixo Central', x: 50, y: 25, position: 'zagueiro', required: true },
            { id: 'zag_r', name: 'Fixo Direito', x: 75, y: 30, position: 'zagueiro', required: true },
            { id: 'at_c', name: 'Pivô', x: 50, y: 70, position: 'atacante', required: true },
          ]
        }
      ],
      neutra: [
        {
          name: '1-2-1',
          totalPlayers: 4,
          positions: { zagueiro: 1, lateral: 0, meioCampo: 2, atacante: 1 },
          description: 'Um fixo, dois alas e um pivô',
          fieldPositions: [
            { id: 'gk', name: 'Goleiro', x: 50, y: 5, position: 'goleiro', required: true },
            { id: 'zag_c', name: 'Fixo', x: 50, y: 25, position: 'zagueiro', required: true },
            { id: 'mc_l', name: 'Ala Esquerdo', x: 30, y: 50, position: 'meio-campo', required: true },
            { id: 'mc_r', name: 'Ala Direito', x: 70, y: 50, position: 'meio-campo', required: true },
            { id: 'at_c', name: 'Pivô', x: 50, y: 75, position: 'atacante', required: true },
          ]
        }
      ]
    }
  }
};

export const getFormations = (groupType: GroupType, formationType: FormationType): Formation[] => {
  return fieldConfigurations[groupType]?.formations[formationType] || [];
};

export const getPlayersPerTeam = (groupType: GroupType): number => {
  return fieldConfigurations[groupType]?.playersPerTeam || 0;
};

export const getRequiredPositions = (groupType: GroupType): PlayerPosition[] => {
  return fieldConfigurations[groupType]?.requiredPositions || [];
};

export const validateLineup = (
  titularPlayers: any[],
  groupType: GroupType,
  formation: Formation
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Verificar se há exatamente 1 goleiro
  const gkCount = titularPlayers.filter(p => p.posicao === 'goleiro').length;
  if (gkCount !== 1) {
    errors.push('A escalação deve ter exatamente 1 goleiro');
  }

  // Verificar posições obrigatórias
  const requiredPositions = getRequiredPositions(groupType);
  for (const reqPos of requiredPositions.filter(p => p !== 'goleiro')) {
    const count = titularPlayers.filter(p => p.posicao === reqPos).length;
    if (count < 1) {
      errors.push(`A escalação deve ter pelo menos 1 jogador na posição de ${reqPos}`);
    }
  }

  // Verificar número total de jogadores
  const expectedTotal = getPlayersPerTeam(groupType);
  if (titularPlayers.length !== expectedTotal) {
    errors.push(`A escalação deve ter exatamente ${expectedTotal} jogadores`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};